import { pgTable, text, uuid, unique, real, foreignKey, jsonb, boolean, index, } from 'drizzle-orm/pg-core';
import { isoTimestamp, sortOrder } from '../../utils';
import { relations, sql } from 'drizzle-orm';
import { RecordType } from '../../enums';
import { orgTableBase, orgPrimaryKey } from '../org-id';
import { BodyPart, Equipment, TrainingType } from '../misc';
import { ExerciseMetric } from '../metrics';
import { Tag } from '../tag';
import { Team } from '../roster/team';
export const LibraryExercise = pgTable('library_exercise', {
    ...orgTableBase,
    name: text('name').notNull(),
    imageUrl: text('image_url'),
    videoUrl: text('video_url'),
    trainingTypeId: uuid('training_type_id').notNull(),
    deletedAt: isoTimestamp('deleted_at'),
    integrationProperties: jsonb('integration_properties')
        .$type()
        .notNull()
        .default({}),
    isDefault: boolean('is_default').notNull().default(false),
    // This contains search metadata for full-text search
    searchVector: text('search_vector'),
    teamId: uuid('team_id'),
    recordType: RecordType('record_type'),
}, (table) => ({
    pk: orgPrimaryKey(table),
    typeFk: foreignKey({
        columns: [table.orgId, table.trainingTypeId],
        foreignColumns: [TrainingType.orgId, TrainingType.id],
    }),
    teamFk: foreignKey({
        columns: [table.orgId, table.teamId],
        foreignColumns: [Team.orgId, Team.id],
    }).onDelete('no action'),
    searchVectorIdx: index('idx_library_exercise_search').using('gin', sql `${table.searchVector}`),
    baseExerciseIdx: index('idx_library_exercise_base_exercises')
        .on(table.orgId)
        .where(sql `${table.teamId} IS NULL`),
}));
export const LibraryExercise_Relations = relations(LibraryExercise, ({ many, one }) => ({
    bodyParts: many(LibraryExerciseBodyPart),
    equipment: many(LibraryExerciseEquipment),
    defaultMetrics: many(LibraryExerciseDefaultMetric),
    tags: many(LibraryExerciseTag),
    team: one(Team, {
        fields: [LibraryExercise.orgId, LibraryExercise.teamId],
        references: [Team.orgId, Team.id],
    }),
    trainingType: one(TrainingType, {
        fields: [LibraryExercise.orgId, LibraryExercise.trainingTypeId],
        references: [TrainingType.orgId, TrainingType.id],
    }),
}));
export const LibraryExerciseBodyPart = pgTable('library_exercise_body_part', {
    ...orgTableBase,
    libraryExerciseId: uuid('library_exercise_id').notNull(),
    bodyPartId: uuid('body_part_id').notNull(),
    sortOrder,
}, (table) => ({
    pk: orgPrimaryKey(table),
    uq: unique().on(table.orgId, table.libraryExerciseId, table.bodyPartId),
    uq2: unique().on(table.orgId, table.libraryExerciseId, table.sortOrder),
    exerciseFk: foreignKey({
        columns: [table.orgId, table.libraryExerciseId],
        foreignColumns: [LibraryExercise.orgId, LibraryExercise.id],
    }),
    bodyPartFk: foreignKey({
        columns: [table.orgId, table.bodyPartId],
        foreignColumns: [BodyPart.orgId, BodyPart.id],
    }),
}));
export const ExerciseBodyPart_Relations = relations(LibraryExerciseBodyPart, ({ one }) => ({
    exercise: one(LibraryExercise, {
        fields: [
            LibraryExerciseBodyPart.orgId,
            LibraryExerciseBodyPart.libraryExerciseId,
        ],
        references: [LibraryExercise.orgId, LibraryExercise.id],
    }),
    bodyPart: one(BodyPart, {
        fields: [
            LibraryExerciseBodyPart.orgId,
            LibraryExerciseBodyPart.bodyPartId,
        ],
        references: [BodyPart.orgId, BodyPart.id],
    }),
}));
export const LibraryExerciseEquipment = pgTable('library_exercise_equipment', {
    ...orgTableBase,
    libraryExerciseId: uuid('library_exercise_id').notNull(),
    equipmentId: uuid('equipment_id').notNull(),
    sortOrder,
}, (table) => ({
    pk: orgPrimaryKey(table),
    uq: unique().on(table.orgId, table.libraryExerciseId, table.equipmentId),
    uq2: unique().on(table.orgId, table.libraryExerciseId, table.sortOrder),
    exerciseFk: foreignKey({
        columns: [table.orgId, table.libraryExerciseId],
        foreignColumns: [LibraryExercise.orgId, LibraryExercise.id],
    }),
    equipmentFk: foreignKey({
        columns: [table.orgId, table.equipmentId],
        foreignColumns: [Equipment.orgId, Equipment.id],
    }),
}));
export const ExerciseEquipment_Relations = relations(LibraryExerciseEquipment, ({ one }) => ({
    exercise: one(LibraryExercise, {
        fields: [
            LibraryExerciseEquipment.orgId,
            LibraryExerciseEquipment.libraryExerciseId,
        ],
        references: [LibraryExercise.orgId, LibraryExercise.id],
    }),
    equipment: one(Equipment, {
        fields: [
            LibraryExerciseEquipment.orgId,
            LibraryExerciseEquipment.equipmentId,
        ],
        references: [Equipment.orgId, Equipment.id],
    }),
}));
export const LibraryExerciseDefaultMetric = pgTable('library_exercise_default_metric', {
    ...orgTableBase,
    libraryExerciseId: uuid('library_exercise_id').notNull(),
    metricId: uuid('metric_id').notNull(),
    sortOrder,
}, (table) => ({
    pk: orgPrimaryKey(table),
    uq: unique().on(table.orgId, table.libraryExerciseId, table.metricId),
    uq2: unique().on(table.orgId, table.libraryExerciseId, table.sortOrder),
    exerciseFk: foreignKey({
        columns: [table.orgId, table.libraryExerciseId],
        foreignColumns: [LibraryExercise.orgId, LibraryExercise.id],
    }),
    categoryFk: foreignKey({
        columns: [table.orgId, table.metricId],
        foreignColumns: [ExerciseMetric.orgId, ExerciseMetric.id],
    }),
}));
export const LibraryExerciseDefaultMetric_Relations = relations(LibraryExerciseDefaultMetric, ({ one }) => ({
    exercise: one(LibraryExercise, {
        fields: [
            LibraryExerciseDefaultMetric.orgId,
            LibraryExerciseDefaultMetric.libraryExerciseId,
        ],
        references: [LibraryExercise.orgId, LibraryExercise.id],
    }),
    metric: one(ExerciseMetric, {
        fields: [
            LibraryExerciseDefaultMetric.orgId,
            LibraryExerciseDefaultMetric.metricId,
        ],
        references: [ExerciseMetric.orgId, ExerciseMetric.id],
    }),
}));
export const LibraryExerciseMaxConversion = pgTable('library_exercise_max_conversion', {
    ...orgTableBase,
    fromLibraryExerciseId: uuid('from_library_exercise_id').notNull(),
    toLibraryExerciseId: uuid('to_library_exercise_id').notNull(),
    ratio: real('ratio').notNull(),
    teamId: uuid('team_id'),
}, (table) => ({
    pk: orgPrimaryKey(table),
    uq: unique().on(table.orgId, table.fromLibraryExerciseId, table.toLibraryExerciseId, table.teamId),
    fromExerciseFk: foreignKey({
        columns: [table.orgId, table.fromLibraryExerciseId],
        foreignColumns: [LibraryExercise.orgId, LibraryExercise.id],
    }),
    toExerciseFk: foreignKey({
        columns: [table.orgId, table.toLibraryExerciseId],
        foreignColumns: [LibraryExercise.orgId, LibraryExercise.id],
    }),
    teamFk: foreignKey({
        columns: [table.orgId, table.teamId],
        foreignColumns: [Team.orgId, Team.id],
    }),
}));
export const LibraryExerciseMaxConversion_Relations = relations(LibraryExerciseMaxConversion, ({ one }) => ({
    fromLibraryExercise: one(LibraryExercise, {
        fields: [
            LibraryExerciseMaxConversion.orgId,
            LibraryExerciseMaxConversion.fromLibraryExerciseId,
        ],
        references: [LibraryExercise.orgId, LibraryExercise.id],
    }),
    toLibraryExercise: one(LibraryExercise, {
        fields: [
            LibraryExerciseMaxConversion.orgId,
            LibraryExerciseMaxConversion.toLibraryExerciseId,
        ],
        references: [LibraryExercise.orgId, LibraryExercise.id],
    }),
    team: one(Team, {
        fields: [
            LibraryExerciseMaxConversion.orgId,
            LibraryExerciseMaxConversion.teamId,
        ],
        references: [Team.orgId, Team.id],
    }),
}));
export const LibraryExerciseTag = pgTable('library_exercise_tag', {
    ...orgTableBase,
    libraryExerciseId: uuid('library_exercise_id').notNull(),
    tagId: uuid('tag_id').notNull(),
    sortOrder,
}, (table) => ({
    pk: orgPrimaryKey(table),
    uq: unique().on(table.orgId, table.libraryExerciseId, table.tagId),
    uq2: unique().on(table.orgId, table.libraryExerciseId, table.sortOrder),
    exerciseFk: foreignKey({
        columns: [table.orgId, table.libraryExerciseId],
        foreignColumns: [LibraryExercise.orgId, LibraryExercise.id],
    }),
    tagFk: foreignKey({
        columns: [table.orgId, table.tagId],
        foreignColumns: [Tag.orgId, Tag.id],
    }),
}));
export const ExerciseTag_Relations = relations(LibraryExerciseTag, ({ one }) => ({
    exercise: one(LibraryExercise, {
        fields: [LibraryExerciseTag.orgId, LibraryExerciseTag.libraryExerciseId],
        references: [LibraryExercise.orgId, LibraryExercise.id],
    }),
    tag: one(Tag, {
        fields: [LibraryExerciseTag.orgId, LibraryExerciseTag.tagId],
        references: [Tag.orgId, Tag.id],
    }),
}));
//# sourceMappingURL=exercises.js.map